/**
 * useNetworkNodesLayer + useTopologyLayer — Sprint 4.2C.
 *
 * NetworkNodes:
 *   POP, OLT, CTO, SPLITTER, OTHER renderizados como L.marker com divIcon
 *   próprio (ícone emoji por tipo + cor por tipo). Tooltip dark com nome.
 *
 * Topology:
 *   Linhas POP → OLT → CTO → Device, derivadas de device_network_links
 *   (mas como o frontend não tem hierarquia explícita POP→OLT→CTO,
 *   desenha apenas device→node ligações). Cor da linha = cor do device
 *   status (verde/laranja/vermelho/cinza).
 */
import { useEffect, useRef } from "react";

const NODE_VISUAL = {
  POP:      { icon: "📡", color: "#a78bfa", label: "POP" },
  OLT:      { icon: "🗄️", color: "#60a5fa", label: "OLT" },
  CTO:      { icon: "📦", color: "#2BD9F0", label: "CTO" },
  SPLITTER: { icon: "🔀", color: "#fbbf24", label: "Splitter" },
  OTHER:    { icon: "⬢",  color: "#94a3b8", label: "Other" },
};

function nodeHtml(node) {
  const v = NODE_VISUAL[node.type] || NODE_VISUAL.OTHER;
  return `<div class="ipwd-network-node" style="--node-color:${v.color}">
    <span class="ipwd-network-node-icon">${v.icon}</span>
  </div>`;
}

/**
 * @param {object} L
 * @param {object} mapRef
 * @param {Array}  nodes
 * @param {object} visibility { POP:bool, OLT:bool, CTO:bool, SPLITTER:bool, OTHER:bool }
 * @param {function} onNodeClick(node)
 */
export function useNetworkNodesLayer({ L, mapRef, nodes, visibility, onNodeClick }) {
  const groupRef = useRef(null);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !L) return;
    if (!groupRef.current) groupRef.current = L.layerGroup().addTo(map);

    const group = groupRef.current;
    group.clearLayers();

    if (!nodes || nodes.length === 0) return;

    for (const n of nodes) {
      if (n.lat == null || n.lng == null) continue;
      if (visibility && visibility[n.type] === false) continue;

      const icon = L.divIcon({
        className: "ipwd-network-node-wrap",
        html: nodeHtml(n),
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });
      const marker = L.marker([n.lat, n.lng], {
        icon,
        zIndexOffset: 200,        // acima de markers de device normais
        riseOnHover: true,
      });
      const v = NODE_VISUAL[n.type] || NODE_VISUAL.OTHER;
      marker.bindTooltip(
        `<b>${v.label}: ${n.name}</b>${n.code ? `<br/><span style="opacity:0.8">${n.code}</span>` : ""}`,
        {
          direction: "top", offset: [0, -14], opacity: 1,
          className: "ipwd-marker-tooltip",
        },
      );
      if (onNodeClick) {
        marker.on("click", () => onNodeClick(n));
      }
      marker.addTo(group);
    }

    return undefined;
  }, [L, mapRef, nodes, visibility, onNodeClick]);

  useEffect(() => {
    return () => {
      const map = mapRef?.current;
      if (map && groupRef.current) {
        map.removeLayer(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [mapRef]);
}


// ============================================================================
// Topology lines
// ============================================================================

const TOPOLOGY_COLOR = {
  online:    "#22c55e",
  relay_off: "#FF8A3C",
  offline:   "#FF5063",
  unknown:   "#64748b",
};

function deviceStatusKey(d) {
  if (!d.online) return "offline";
  if (d.relay === "off") return "relay_off";
  if (d.online) return "online";
  return "unknown";
}

/**
 * Desenha linhas POP→OLT→CTO→Device. Como não temos hierarquia explícita
 * entre nodes (sprint futura), por enquanto desenhamos LINHAS device→node
 * baseado em device_network_links (passado via prop deviceLinksMap).
 *
 * @param {object} L
 * @param {object} mapRef
 * @param {bool}   visible
 * @param {Map<string, Array<DeviceNetworkLinkOut>>} deviceLinksMap
 *      Map<device_id, [links]>
 * @param {Map<string, NetworkNodeOut>} nodesById
 * @param {Map<string, FleetMapDeviceOut>} devicesById
 */
export function useTopologyLayer({
  L, mapRef, visible, deviceLinksMap, nodesById, devicesById,
}) {
  const groupRef = useRef(null);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !L) return;
    if (!groupRef.current) groupRef.current = L.layerGroup().addTo(map);

    const group = groupRef.current;
    group.clearLayers();
    if (!visible) return;

    for (const [deviceId, links] of deviceLinksMap.entries()) {
      const d = devicesById.get(deviceId);
      if (!d || d.lat == null || d.lng == null) continue;
      const color = TOPOLOGY_COLOR[deviceStatusKey(d)] || TOPOLOGY_COLOR.unknown;

      for (const link of links) {
        const n = nodesById.get(link.node_id);
        if (!n || n.lat == null || n.lng == null) continue;
        const line = L.polyline(
          [[d.lat, d.lng], [n.lat, n.lng]],
          {
            color,
            weight: 2,
            opacity: 0.7,
            dashArray: link.link_type === "nearby" ? "4,4" : null,
            className: "ipwd-topology-line",
          },
        );
        line.bindTooltip(
          `<b>${d.device_id}</b> → ${n.name} <span style="opacity:0.7">(${link.link_type})</span>`,
          { className: "ipwd-marker-tooltip", direction: "center", sticky: true },
        );
        line.addTo(group);
      }
    }

    return undefined;
  }, [L, mapRef, visible, deviceLinksMap, nodesById, devicesById]);

  useEffect(() => {
    return () => {
      const map = mapRef?.current;
      if (map && groupRef.current) {
        map.removeLayer(groupRef.current);
        groupRef.current = null;
      }
    };
  }, [mapRef]);
}
